'use client'

import { useEffect, type RefObject } from 'react'

/**
 * Вызывает onOutside при клике вне элемента ref.
 * Заменяет ~29 копий document.addEventListener('mousedown', ...) + проверка ref.
 *
 * Параметры:
 *   ref       — элемент, клики внутри которого игнорируются
 *   onOutside — колбэк при клике снаружи
 *   opts.active   — слушать только когда true (например когда дропдаун открыт)
 *   opts.armDelay — задержка перед навешиванием слушателя (мс). Нужна, когда тот
 *                   же клик, что открыл попап, иначе сразу его закрывает.
 *                   undefined → навесить синхронно; 0 → отложить на следующий tick
 *                   (как setTimeout(…,0)); 50/100 → явная задержка.
 *   opts.event    — 'mousedown' (по умолчанию) | 'click'
 *   opts.extraRefs — доп. элементы-исключения (триггер-кнопка вне панели)
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  onOutside: () => void,
  opts?: {
    active?: boolean
    armDelay?: number
    event?: 'mousedown' | 'click'
    extraRefs?: Array<RefObject<HTMLElement | null>>
  },
) {
  const active = opts?.active ?? true
  const armDelay = opts?.armDelay
  const event = opts?.event ?? 'mousedown'

  useEffect(() => {
    if (!active) return
    if (typeof document === 'undefined') return

    function handler(e: Event) {
      const target = e.target as Node
      if (ref.current?.contains(target)) return
      // Выпадающая панель поля (components/ui/floating.ts) рисуется порталом,
      // вне разметки своего хозяина: выбор пункта в ней — не клик снаружи.
      if (target instanceof Element && target.closest('[data-floating-panel]')) return
      for (const r of opts?.extraRefs ?? []) {
        if (r.current?.contains(target)) return
      }
      onOutside()
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    const arm = () => document.addEventListener(event, handler)
    if (armDelay === undefined) arm()
    else timer = setTimeout(arm, armDelay)

    return () => {
      if (timer) clearTimeout(timer)
      document.removeEventListener(event, handler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, armDelay, event, onOutside])
}
